import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    practiceName: '',
    practiceSpecialty: '',
    serviceNeeded: '',
    otherService: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const serviceValue = formData.serviceNeeded === 'Other' 
        ? formData.otherService 
        : formData.serviceNeeded;

      const formBody = new URLSearchParams({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        practiceName: formData.practiceName,
        practiceSpecialty: formData.practiceSpecialty,
        serviceNeeded: serviceValue,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5si62k6pqudvsuu5510', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          practiceName: '',
          practiceSpecialty: '',
          serviceNeeded: '',
          otherService: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-red-50 via-white to-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Schedule Your Assessment
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Take the first step toward securing your practice and reducing compliance risk. Fill out the form below and we'll be in touch within 24 hours.
            </p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 bg-white">
          <div className="max-w-2xl mx-auto px-6">
            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                    placeholder="John"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                  placeholder="john.smith@example.com"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="practiceName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Practice Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="practiceName"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                  placeholder="ABC Medical Center"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="practiceSpecialty" className="block text-sm font-semibold text-gray-700 mb-2">
                  Practice Area/Specialty <span className="text-red-500">*</span>
                </label>
                <select
                  id="practiceSpecialty"
                  name="practiceSpecialty"
                  value={formData.practiceSpecialty}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm bg-white"
                >
                  <option value="">Select your specialty</option>
                  <option value="Allergy & Asthma">Allergy & Asthma</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Dermatology">Dermatology</option>
                  <option value="ENT">ENT</option>
                  <option value="Esthetician/Aesthetician">Esthetician/Aesthetician</option>
                  <option value="Family Medicine">Family Medicine</option>
                  <option value="Internal Medicine">Internal Medicine</option>
                  <option value="MedSpa">MedSpa</option>
                  <option value="Orthopedics">Orthopedics</option>
                  <option value="Pain Management">Pain Management</option>
                  <option value="Pediatrics">Pediatrics</option>
                  <option value="Physical Therapy">Physical Therapy</option>
                  <option value="Plastic Surgery">Plastic Surgery</option>
                  <option value="Podiatry">Podiatry</option>
                  <option value="Psychiatry">Psychiatry</option>
                  <option value="Rural Hospital">Rural Hospital</option>
                  <option value="Urgent Care">Urgent Care</option>
                  <option value="Veterinary">Veterinary</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="service" className="block text-sm font-semibold text-gray-900 mb-2">
                  Which Service Are You Needing Help With? <span className="text-red-500">*</span>
                </label>
                <select
                  id="service"
                  name="serviceNeeded"
                  required
                  value={formData.serviceNeeded}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent text-gray-900 bg-white text-sm"
                >
                  <option value="">Select a service</option>
                  <option value="IT / Cybersecurity / Networking">IT / Cybersecurity / Networking</option>
                  <option value="VoIP & Communication Services">VoIP & Communication Services</option>
                  <option value="HR Services">HR Services</option>
                  <option value="Fractional Operations Manager">Fractional Operations Manager</option>
                  <option value="Banking & Financing">Banking & Financing</option>
                  <option value="Fractional CFO">Fractional CFO</option>
                  <option value="Marketing & Growth">Marketing & Growth</option>
                  <option value="New office buildout">New office buildout</option>
                  <option value="Office move">Office move</option>
                  <option value="Construction">Construction</option>
                  <option value="Other">Other (please specify)</option>
                </select>
              </div>

              {formData.serviceNeeded === 'Other' && (
                <div className="mb-8">
                  <label htmlFor="otherService" className="block text-sm font-semibold text-gray-700 mb-2">
                    Please Specify <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="otherService"
                    name="otherService"
                    value={formData.otherService}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e10708] focus:border-transparent transition-all text-sm"
                    placeholder="Please describe the service you need"
                  />
                </div>
              )}

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">
                    ✓ Thank you! We've received your information and will contact you within 24 hours.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">
                    There was an error submitting your form. Please try again or contact us directly.
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#e10708] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#c00607] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>

              <p className="text-sm text-gray-500 text-center mt-6">
                By submitting this form, you agree to be contacted by Healthcare IT Partners regarding your assessment.
              </p>
            </form>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What Happens Next?
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-check-line text-[#e10708] text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">1. We Review</h3>
                <p className="text-gray-600">
                  Our team reviews your information and prepares a customized assessment plan
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-line text-[#e10708] text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">2. We Connect</h3>
                <p className="text-gray-600">
                  We'll reach out within 24 hours to schedule your initial consultation
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-[#e10708] text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">3. We Protect</h3>
                <p className="text-gray-600">
                  Begin your journey toward comprehensive security and compliance
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}